import React, { CSSProperties, createElement } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Serializer, Question, ElementFactory, IElement, SvgRegistry } from 'survey-core';
import { SurveyQuestionElementBase, ReactQuestionFactory } from 'survey-react-ui';
import { SurveyCreator } from 'survey-creator-react';
import { CroppedImage, ImageCropForUser } from 'vl-common/src/components/CustomSurvey/customWidgets/ImageCrop';
import { localization } from 'survey-creator-core';
import { MdCrop } from 'react-icons/md';
import { debounce } from 'lodash';
import { FlatRepository, FlatQuestion, TextBoldBrick, TextBrick } from 'survey-pdf';

// This component is React based - follows https://surveyjs.io/form-library/documentation/customize-question-types/third-party-component-integration-react

const CUSTOM_TYPE = 'region-of-interest';

SvgRegistry.registerIconFromSvg(CUSTOM_TYPE, ReactDOMServer.renderToString(<MdCrop />));
const locale = localization.getLocale('');
locale.qt[CUSTOM_TYPE] = 'Region-of-Interest';
locale.pe.fileUploadLabel = 'File Upload Label';
locale.pe.dimBackground = 'Dim Background';

// Specifies all the "class" properties of a Region of Interest
export class RegionOfInterestModel extends Question {
  getType() {
    return CUSTOM_TYPE;
  }

  get fileUploadLabel() {
    return this.getPropertyValue('fileUploadLabel');
  }

  set fileUploadLabel(val: string) {
    this.setPropertyValue('fileUploadLabel', val);
  }

  get dimBackground() {
    return this.getPropertyValue('dimBackground');
  }

  set dimBackground(val: boolean) {
    this.setPropertyValue('dimBackground', val);
  }
}

Serializer.addClass(
  CUSTOM_TYPE,
  [
    {
      name: 'fileUploadLabel',
      default: 'Choose Images',
      category: 'general',
      visibleIndex: 2 // Place after the Name and Title
    },
    {
      name: 'dimBackground:boolean',
      default: true,
      category: 'general',
      visibleIndex: 3 // Place after the Name and Title
    }
  ],
  function () {
    return new RegionOfInterestModel('');
  },
  'question'
);

// All the image properties are stored under the "value" property
export class SurveyRegionOfInterest extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.state = { value: this.question.value, model: props.creator.props?.model };
  }
  get question() {
    return this.questionBase;
  }
  get value() {
    return this.question.value;
  }

  get fileUploadLabel() {
    return this.question.fileUploadLabel;
  }

  get dimBackground() {
    return this.question.dimBackground;
  }

  // Support the read-only and design modes
  get style() {
    return this.question.getPropertyValue('readOnly') || this.question.isDesignMode
      ? ({ pointerEvents: 'none' } as CSSProperties)
      : undefined;
  }

  renderElement() {
    const downloadImage = async (image: CroppedImage) => {
      return await this.state.model.onRegionOfInterestImageDownload(image);
    };

    const changed = async (images: CroppedImage[], callback?: (updatedImages: CroppedImage[]) => void) => {
      // Upload freshly added images that don't have 'content' (the S3 temp link)
      let uploadedImages = [...images];
      if (images.some((image) => !image.content)) {
        uploadedImages = await this.state?.model.onRegionOfInterestImageUpload(images);
      }
      // Callback supplied will update the croppedImages state in ImageCrop with updated/formatted images
      callback?.(uploadedImages);
      this.question.value = uploadedImages;
    };

    return (
      <div style={this.style}>
        <ImageCropForUser
          images={Array.isArray(this.value) ? this.value : []}
          onChange={debounce(changed, 500)}
          uploadLabel={this.fileUploadLabel}
          dimBackground={this.dimBackground}
          downloadImage={downloadImage}
        />
      </div>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
  return createElement(SurveyRegionOfInterest, props);
});

// this bit is needed to tell the survey creator in vl-admin about the new component
const CustomizeSurveyRegionsOfInterest = (surveyCreator?: SurveyCreator) => {
  ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name: string) => {
    return new RegionOfInterestModel(name) as IElement;
  });

  if (surveyCreator && !surveyCreator.toolbox.getItemByName(CUSTOM_TYPE)) {
    surveyCreator.toolbox.addItem({
      name: CUSTOM_TYPE,
      title: CUSTOM_TYPE,
      iconName: '',
      json: { type: CUSTOM_TYPE },
      category: 'Custom Type',
      className: 'svd_custom_type',
      isCopied: false
    });
  }
};

export default CustomizeSurveyRegionsOfInterest;

class FlatCustomROI extends FlatQuestion {
  async generateFlats(point: any) {
    const values = this.question.value;

    const rect = { ...point };
    rect.yBot = point.yTop + 20;
    rect.xRight = point.xLeft + 20;

    if (!Array.isArray(values) || values.length === 0) return [];

    const bricks = [];
    let y = point.yTop;

    const titleBrick = new TextBoldBrick(this.question, this.controller, rect, this.question.title);
    bricks.push(titleBrick);

    const contentBrick = new TextBrick(
      this.question,
      this.controller,
      rect,
      "You can view the images you've uploaded on your 'Documents' page, which you can access from your dashboard."
    );

    y += titleBrick.height + 16;

    contentBrick.yTop = y;
    bricks.push(contentBrick);

    return bricks;
  }
}

FlatRepository.register(CUSTOM_TYPE, FlatCustomROI);
